import { IonBackButton, IonButton, IonButtons, IonIcon, IonSpinner } from "@ionic/react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleMapContext } from "@src/app/context/googleMapContext";
import { getDeviceGeoLocation } from "@src/appV2/Location";
import { GoogleMapErrorBoundary } from "@src/appV2/redesign/GeoLocation/GoogleMapErrorBoundary";
import { LA_CENTER } from "@src/lib/deprecatedCode";
import { locate } from "ionicons/icons";
import { isEqual } from "lodash";
import { Fragment, memo, useEffect, useMemo, useRef, useState } from "react";

import { useMapControl } from "./useMapControl";

type LatLng = {
  lng: number;
  lat: number;
};

const googleMapContainerStyle = {
  width: "100%",
  height: "100%",
};

const AddressEditMapComponent = ({ location, onChange }) => {
  const [mapCenter, setMapCenter] = useState(LA_CENTER);
  const [markerLocation, setMarkerLocation] = useState<LatLng>();
  const { isMapLoaded } = useGoogleMapContext();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [position, setPosition] = useState<google.maps.ControlPosition.RIGHT_BOTTOM | null>(null);

  const onCurrentLocationControl = async () => {
    const { geoLocation } = await getDeviceGeoLocation();
    const lat = geoLocation.latitude ?? "";
    const lng = geoLocation.longitude ?? "";
    onChange({ lat, lng });
  };

  const onCurrentLocationControlRef = useRef(onCurrentLocationControl);
  onCurrentLocationControlRef.current = onCurrentLocationControl;

  const MapButton = useMemo(() => {
    return (
      <IonButton fill="clear" onClick={() => onCurrentLocationControlRef.current()}>
        <IonIcon style={{ fontSize: "30px" }} icon={locate} mode="md" />
      </IonButton>
    );
  }, []);

  useMapControl(map, position, MapButton);

  useEffect(() => {
    setMarkerLocation(location);
    setMapCenter(location || LA_CENTER);
  }, [location]);

  const onDrag = () => {
    setMarkerLocation({
      lat: map?.getCenter()?.lat() as number,
      lng: map?.getCenter()?.lng() as number,
    });
  };

  const onMapChange = () => {
    if (isEqual(location, markerLocation)) {
      return;
    }

    onChange({ ...markerLocation });
  };

  const handleOnGoogleApiLoaded = (mountedMap) => {
    setPosition(google.maps.ControlPosition.RIGHT_BOTTOM);
    setMap(mountedMap);
  };

  if (!isMapLoaded) {
    return (
      <div style={{ textAlign: "center", marginTop: "10em" }}>
        <IonSpinner color="primary" />
      </div>
    );
  }

  return (
    <Fragment>
      <div style={{ position: "absolute", left: "10px", top: "10px", zIndex: 999 }}>
        <IonButtons slot="start">
          <IonBackButton
            text=""
            color="dark"
            defaultHref="/home/account/searchAddressLocation"
            mode="ios"
          />
        </IonButtons>
      </div>
      <GoogleMapErrorBoundary>
        <GoogleMap
          mapContainerStyle={googleMapContainerStyle}
          center={mapCenter}
          zoom={17}
          onBoundsChanged={onMapChange}
          options={{
            disableDefaultUI: true,
          }}
          onDrag={onDrag}
          onLoad={handleOnGoogleApiLoaded}
        >
          {markerLocation && <Marker position={markerLocation} />}
        </GoogleMap>
      </GoogleMapErrorBoundary>
    </Fragment>
  );
};

export const AddressEditMap = memo(AddressEditMapComponent);
